import React, { useEffect, useState } from 'react';
import { GetPositionsResultItem } from 'config/api';
import * as useAPI from 'hooks/useAPI';
import { commaFormat } from '../new-report/utils/formatter';
import { useParams } from 'react-router-dom';
import { Transaction, useGetTransactions } from 'hooks/useGetTransactions';
import { CURRENCY, TransactionsCategoryType } from 'config/consts';

type PositionListTransactionItemProps = {
  iconClassName: string;
  title: string;
  date: Date;
  amount: number;
  amountUSD: number | null;
};

export const transactionsCategoryTypeToIconClassName = (
  category: typeof TransactionsCategoryType[keyof typeof TransactionsCategoryType]
): string => {
  const value = ((): string => {
    if (category === TransactionsCategoryType['住宅']) return `${category}1`;
    return category;
  })();
  return `icon-${value}`;
};

const PositionListTransactionItem = ({
  amount,
  amountUSD,
  date,
  iconClassName,
  title,
}: PositionListTransactionItemProps): React.ReactElement => {
  const dateStr = `${date.getFullYear()}/${date.getMonth() +
    1}/${date.getDate()}`;
  const amountColor = amount > 0 ? 'text-upcolor' : 'text-downcolor'; // TODO: マージしてから共通化する
  const getAmountStr = (amount: number): string => {
    const prefix = amount > 0 ? '+' : '-';
    const absStr = commaFormat(Math.abs(amount), { numberOfDecimals: 0 });
    return `${prefix}${absStr}`;
  };
  const amountStr: string = getAmountStr(amount); // TODO: マージしてから共通化する
  const amountUSDStr: string | null = amountUSD
    ? getAmountStr(amountUSD)
    : null;
  return (
    <>
      <div className={`pt-2 pb-2 position-list-transaction-item`}>
        <div
          className={`${iconClassName} w-8 h-8 rounded-full grid items-center justify-center bg-gray-500 text-lg text-white position-list-transaction-item-icon`}></div>
        <div className="font-bold text-base pt-1 pb-1 justify-start align-middle position-list-transaction-item-title">
          {title}
        </div>
        <div className="pr-2 align-middle position-list-transaction-item-data">
          <div className="font-normal text-sm whitespace-no-wrap align-middle position-list-transaction-item-date">
            {dateStr}
          </div>
          <div
            className={`${amountColor} align-middle col-gap-2 position-list-transaction-item-amount`}>
            <div className="text-lg">{amountStr}</div>
            <div className="text-base">円</div>
          </div>
        </div>
        {amountUSDStr && (
          <div className="pr-2 align-middle position-list-transaction-foreign-item-data">
            <div
              className={`align-middle position-list-transaction-foreign-item-amount ${amountColor}`}>
              (<div className="text-base mr-1">{amountUSDStr}</div>
              <div className="text-sm">{CURRENCY.米ドル}</div>)
            </div>
          </div>
        )}
      </div>
    </>
  );
};

type PositionListTransactionGroupProps = {
  year: number;
  month: number;
  items: Transaction[];
};

const PositionListTransactionGroupTitle = ({
  year,
  month,
}: {
  year: number;
  month: number;
}): React.ReactElement => {
  return (
    <div
      className="bg-gray-100 py-2 px-8 border-gray-300 border-t border-b"
      style={{ color: '#64748B' }}>
      {year}年{month}月
    </div>
  );
};

const interleave = <T,>(separater: T) => (arr: T[]): T[] => {
  return arr.flatMap(x => [separater, x]).slice(1);
};

const PositionListTransactionGroup = ({
  year,
  month,
  items,
}: PositionListTransactionGroupProps): React.ReactElement => {
  return (
    <>
      <PositionListTransactionGroupTitle {...{ year, month }} />
      {interleave(
        <>
          <div
            className="w-auto mx-4 bg-gray-200"
            style={{
              height: '1px',
            }}
          />
        </>
      )(
        items.map((item, i, arr) => {
          return (
            <div className="mx-3">
              <div className="mx-5">
                <PositionListTransactionItem
                  {...{
                    amount: item.amount,
                    amountUSD: item.amountUSD,
                    date: item.datetime,
                    iconClassName: transactionsCategoryTypeToIconClassName(
                      item.category
                    ),
                    title: item.description,
                    key: i,
                  }}
                />
              </div>
            </div>
          );
        })
      )}
    </>
  );
};

const EmptyGroup = ({
  year,
  month,
}: {
  year: number;
  month: number;
}): React.ReactElement => {
  return (
    <>
      <PositionListTransactionGroupTitle {...{ year, month }} />
      <div className="mx-3">
        <div className="mx-0">
          <div className="bg-gray-100 rounded border-gray-300 border text-center p-5 mt-4">
            当月の入出金データは存在しません
          </div>
        </div>
      </div>
    </>
  );
};

export const group_ = <T, Key>(
  values: T[],
  toComparableKey: (val: T) => Key,
  equalityFn: (a: Key) => (b: Key) => boolean,
  initialKey: Key
): { key: Key; items: T[] }[] => {
  const go = ({
    prev,
    remain,
    result,
  }: {
    prev: { key: Key; items: T[] };
    remain: T[];
    result: { key: Key; items: T[] }[];
  }): { key: Key; items: T[] }[] => {
    if (remain.length === 0) return [...result, prev];
    const [head, ...tail] = remain;
    if (equalityFn(toComparableKey(head))(prev.key))
      return go({
        prev: { ...prev, items: [...prev.items, head] },
        remain: tail,
        result,
      });
    return go({
      prev: {
        key: toComparableKey(head),
        items: [head],
      },
      remain: tail,
      result: [...result, prev],
    });
  };
  return trampoline(go)({
    prev: { key: initialKey, items: [] },
    remain: values,
    result: [],
  }).filter((item: { key: Key; items: T[] }) => item.items.length > 0);
};

const group = (
  transactions: Transaction[]
): { key: { year: number; month: number }; items: Transaction[] }[] => {
  return group_(
    transactions.sort((a, b) => a.datetime.getTime() - b.datetime.getTime()),
    (val): { year: number; month: number } => ({
      year: val.datetime.getFullYear(),
      month: val.datetime.getMonth() + 1,
    }),
    ({ year: yearA, month: monthA }) => ({
      year: yearB,
      month: monthB,
    }): boolean => yearA === yearB && monthA === monthB,
    { year: 0, month: 0 }
  );
};

const monthRegex = /^(\d{4})(\d{2})$/;

const trampoline = <Args extends unknown[], Ret>(
  fn: (...args: Args) => Ret
) => {
  return (...args: Args) => {
    let result = fn(...args);
    while (typeof result === 'function') {
      result = result();
    }
    return result;
  };
};

const getAvailableMonths = (
  start: { year: number; month: number },
  end: { year: number; month: number }
): { year: number; month: number }[] => {
  const go = (acc: {
    prev: { year: number; month: number };
    result: { year: number; month: number }[];
  }): { year: number; month: number }[] => {
    if (acc.prev.year === end.year && acc.prev.month === end.month)
      return acc.result;
    if (acc.prev.year * 100 + acc.prev.month > end.year * 100 + end.month)
      return acc.result;
    const current = {
      year: acc.prev.month === 12 ? acc.prev.year + 1 : acc.prev.year,
      month: acc.prev.month === 12 ? 1 : acc.prev.month + 1,
    };
    return go({
      prev: current,
      result: [...acc.result, current],
    });
  };
  return trampoline(go)({ prev: start, result: [start] });
};

const monthToOptionValue = ({
  year,
  month,
}: {
  year: number;
  month: number;
}): string => {
  return `${year}${month.toString().padStart(2, '0')}`;
};

const optionValueToMonth = (
  val: string
): { year: number; month: number } | null => {
  const result = monthRegex.exec(val);
  if (!result) return null;
  const year = Number(result[1]);
  const month = Number(result[2]);
  if (year < 2000 || month < 1 || month > 12) return null;
  return {
    year,
    month,
  };
};

export const PositionListTransaction = (_: {
  positions: Array<GetPositionsResultItem>;
}): React.ReactElement | null => {
  const { transactions, getTransactions } = useGetTransactions();
  const [optionValue, setOptionValue] = useState<string | null>(null);
  const { account } = useParams<{ account: string }>();
  useEffect(() => {
    getTransactions({ accountId: account });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (
    useAPI.isLoading(transactions) ||
    transactions.transactions.items.length === 0
  )
    return <></>;

  const { oldest, latest } = ((
    ts: Transaction[]
  ): { oldest: Date; latest: Date } => {
    const ts_ = ts.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
    return {
      oldest: ts_[0].datetime,
      latest: ts_[ts_.length - 1].datetime,
    };
  })(transactions.transactions.items);
  const availableMonths = getAvailableMonths(
    { year: oldest.getFullYear(), month: oldest.getMonth() + 1 },
    { year: latest.getFullYear(), month: latest.getMonth() + 1 }
  ).sort((a, b) => b.year * 100 + b.month - (a.year * 100 + a.month));
  const options = availableMonths.map(({ year, month }, index) => {
    return (
      <option value={monthToOptionValue({ year, month })} key={index}>
        {year}年{month}月
      </option>
    );
  });
  const onSelect: React.ChangeEventHandler<HTMLSelectElement> = event => {
    const value = event.target.value === noValue ? null : event.target.value;
    setOptionValue(value);
  };
  const noValue = '何も選択されていません';
  const optMonth = optionValue ? optionValueToMonth(optionValue) : null;
  const optionValueFiltered = group(transactions.transactions.items)
    .filter(({ key }) => {
      const { year, month } = key;
      if (optionValue === null) return true;
      return optMonth?.year === year && optMonth?.month === month;
    })
    .sort(
      (a, b) =>
        b.key.year * 100 + b.key.month - (a.key.year * 100 + a.key.month)
    );
  return (
    <>
      <div className="px-3 ui-border-t">
        <div className="flex items-center">
          <div className="flex-grow py-3 px-4">履歴</div>
          <div className="relative">
            <select
              className="appearance-none h-8 w-48 px-4 bg-gray-200 border border-gray-300 rounded cursor-pointer"
              value={optionValue ?? noValue}
              onChange={onSelect}>
              <option value={noValue} key={noValue}>
                選択されていません
              </option>
              {options}
            </select>
            <div className="absolute pointer-events-none inset-y-0 right-0 flex items-center pr-1">
              <i className="icon-arrow_drop_down text-xl text-gray-700" />
            </div>
          </div>
        </div>
      </div>
      {optionValueFiltered.map(({ key, items }) => {
        return (
          <PositionListTransactionGroup
            {...{ ...key, items }}
            key={monthToOptionValue(key)}
          />
        );
      })}
      {!optionValueFiltered.length && optMonth && <EmptyGroup {...optMonth} />}
    </>
  );
};
